// Facebook Pixel and Analytics Tracking Utilities
// Pixel ID: 168374735109597

// Initialize Facebook Pixel
export const initFacebookPixel = () => {
  const PIXEL_ID = '168374735109597';
  
  // Check if already initialized
  if (window.fbq) return;
  
  // Facebook Pixel base code
  (function(f, b, e, v, n, t, s) {
    if (f.fbq) return;
    n = f.fbq = function() {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = !0;
    n.version = '2.0';
    n.queue = [];
    t = b.createElement(e);
    t.async = !0;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
  
  window.fbq('init', PIXEL_ID);
  window.fbq('track', 'PageView');
  
};

// Initialize Google Analytics 4
export const initGoogleAnalytics = (measurementId) => {
  if (!measurementId) return;
  
  // Check if already initialized
  if (window.gtag) return;
  
  // Load gtag.js
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  document.head.appendChild(script);
  
  // Initialize gtag
  window.dataLayer = window.dataLayer || [];
  window.gtag = function() {
    window.dataLayer.push(arguments);
  };
  window.gtag('js', new Date());
  window.gtag('config', measurementId);
  
};

// ============== Facebook Pixel Events ==============

// Track page view (called automatically on route change)
export const trackPageView = (pageName) => {
  if (window.fbq) {
    window.fbq('track', 'PageView');
  }
  if (window.gtag) {
    window.gtag('event', 'page_view', {
      page_title: pageName,
      page_location: window.location.href
    });
  }
  // First-party tracker — feeds /admin/website-analytics
  try {
    let sid = sessionStorage.getItem('oct_sid');
    if (!sid) {
      sid = (crypto.randomUUID ? crypto.randomUUID() : `s-${Date.now()}-${Math.random().toString(36).slice(2)}`);
      sessionStorage.setItem('oct_sid', sid);
    }
    const apiBase = process.env.REACT_APP_BACKEND_URL;
    if (apiBase) {
      // Don't track admin/field/portal internal navigation — focus on customer-facing site
      const path = window.location.pathname;
      const internal = path.startsWith('/admin') || path.startsWith('/field') || path.startsWith('/employee-portal') || path.startsWith('/portal');
      const body = JSON.stringify({
        path,
        title: pageName || document.title,
        referrer: document.referrer || '',
        session_id: sid,
        is_internal: internal,
      });
      // Use sendBeacon when available (survives navigation); fall back to fetch
      const url = `${apiBase}/api/website-analytics/pageview`;
      if (navigator.sendBeacon) {
        const blob = new Blob([body], { type: 'application/json' });
        navigator.sendBeacon(url, blob);
      } else {
        fetch(url, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body,
          keepalive: true,
        }).catch(() => {});
      }
    }
  } catch { /* never break the app over analytics */ }
};

// Track when user views a service/calculator
export const trackViewContent = (contentType, contentName, value = 0) => {
  if (window.fbq) {
    window.fbq('track', 'ViewContent', {
      content_type: contentType,
      content_name: contentName,
      value: value,
      currency: 'USD'
    });
  }
  if (window.gtag) {
    window.gtag('event', 'view_item', {
      content_type: contentType,
      item_name: contentName,
      value: value
    });
  }
};

// Track when user starts a calculator/quote process
export const trackInitiateCheckout = (serviceName, value) => {
  if (window.fbq) {
    window.fbq('track', 'InitiateCheckout', {
      content_name: serviceName,
      value: value,
      currency: 'USD'
    });
  }
  if (window.gtag) {
    window.gtag('event', 'begin_checkout', {
      items: [{ item_name: serviceName }],
      value: value
    });
  }
};

// Track when user submits an estimate request (LEAD)
export const trackLead = (serviceName, value, email = '') => {
  if (window.fbq) {
    window.fbq('track', 'Lead', {
      content_name: serviceName,
      value: value,
      currency: 'USD'
    });
  }
  if (window.gtag) {
    window.gtag('event', 'generate_lead', {
      currency: 'USD',
      value: value,
      service_name: serviceName
    });
  }
};

// Track when user schedules/books a service
export const trackSchedule = (serviceName, value) => {
  if (window.fbq) {
    window.fbq('track', 'Schedule', {
      content_name: serviceName,
      value: value,
      currency: 'USD'
    });
  }
  if (window.gtag) {
    window.gtag('event', 'purchase', {
      currency: 'USD',
      value: value,
      items: [{ item_name: serviceName }]
    });
  }
};

// Track when user completes a purchase/payment
export const trackPurchase = (serviceName, value, transactionId) => {
  if (window.fbq) {
    window.fbq('track', 'Purchase', {
      content_name: serviceName,
      value: value,
      currency: 'USD'
    });
  }
  if (window.gtag) {
    window.gtag('event', 'purchase', {
      transaction_id: transactionId,
      currency: 'USD',
      value: value,
      items: [{ item_name: serviceName }]
    });
  }
};

// Track user registration/signup
export const trackCompleteRegistration = (method = 'email') => {
  if (window.fbq) {
    window.fbq('track', 'CompleteRegistration', {
      content_name: 'User Signup',
      status: true
    });
  }
  if (window.gtag) {
    window.gtag('event', 'sign_up', {
      method: method
    });
  }
};

// Track email capture (for popup)
export const trackEmailCapture = (source = 'popup') => {
  if (window.fbq) {
    window.fbq('track', 'Lead', {
      content_name: 'Email Capture',
      content_category: source
    });
  }
  if (window.gtag) {
    window.gtag('event', 'generate_lead', {
      lead_source: source
    });
  }
};

// Track custom events
export const trackCustomEvent = (eventName, params = {}) => {
  if (window.fbq) {
    window.fbq('trackCustom', eventName, params);
  }
  if (window.gtag) {
    window.gtag('event', eventName, params);
  }
};

// ============== Google Ads Remarketing & Conversion ==============

// Initialize Google Ads remarketing tag
export const initGoogleAds = (awId) => {
  if (!awId) return;
  // gtag.js is already loaded by GA4; just add the Ads config
  if (!window.gtag) return;
  window.gtag('config', awId);
};

// Track a Google Ads conversion event
export const trackGoogleAdsConversion = (awId, conversionLabel, value = 0) => {
  if (!window.gtag || !awId) return;
  window.gtag('event', 'conversion', {
    send_to: `${awId}/${conversionLabel}`,
    value: value,
    currency: 'USD'
  });
};

// Convenience: track a Click-to-Call conversion ($20 value per Google Ads config)
export const trackClickToCall = () => {
  const awId = process.env.REACT_APP_GOOGLE_ADS_ID;
  const label = process.env.REACT_APP_GOOGLE_ADS_CONVERSION_LABEL;
  if (awId && label) {
    trackGoogleAdsConversion(awId, label, 20);
  }
  // Also track as GA4 event
  if (window.gtag) {
    window.gtag('event', 'click_to_call', { event_category: 'engagement', value: 20 });
  }
};

// Convenience: track an estimate request as a Google Ads conversion
export const trackEstimateConversion = (value = 0) => {
  const awId = process.env.REACT_APP_GOOGLE_ADS_ID;
  const label = process.env.REACT_APP_GOOGLE_ADS_CONVERSION_LABEL;
  if (awId && label) {
    trackGoogleAdsConversion(awId, label, value);
  }
};

// Master initializer — call after cookie consent is granted
export const initAllTracking = () => {
  initFacebookPixel();
  initGoogleAnalytics('G-C4L6JNJXM9');
  const awId = process.env.REACT_APP_GOOGLE_ADS_ID;
  if (awId) initGoogleAds(awId);
};

// ============== Visitor Tracking ==============

// Generate or retrieve visitor ID (stored in localStorage)
export const getVisitorId = () => {
  let visitorId = localStorage.getItem('oc_visitor_id');
  if (!visitorId) {
    visitorId = 'v_' + Math.random().toString(36).substr(2, 9) + Date.now().toString(36);
    localStorage.setItem('oc_visitor_id', visitorId);
  }
  return visitorId;
};

// Track visitor behavior (pages visited, time spent, etc.)
export const trackVisitorBehavior = (action, data = {}) => {
  const visitorId = getVisitorId();
  const behavior = JSON.parse(localStorage.getItem('oc_visitor_behavior') || '[]');
  
  behavior.push({
    visitorId,
    action,
    data,
    timestamp: new Date().toISOString(),
    page: window.location.pathname
  });
  
  // Keep only last 50 actions
  if (behavior.length > 50) {
    behavior.shift();
  }
  
  localStorage.setItem('oc_visitor_behavior', JSON.stringify(behavior));
  
  return behavior;
};

// Get visitor's browsing history
export const getVisitorHistory = () => {
  return JSON.parse(localStorage.getItem('oc_visitor_behavior') || '[]');
};

// Check if visitor has captured email
export const hasEmailCaptured = () => {
  return localStorage.getItem('oc_email_captured') === 'true';
};

// Mark email as captured
export const setEmailCaptured = (email) => {
  localStorage.setItem('oc_email_captured', 'true');
  localStorage.setItem('oc_captured_email', email);
  trackEmailCapture('popup');
};

// Check if popup has been shown recently
export const shouldShowEmailPopup = () => {
  // Don't show popup to logged-in users (they already have accounts)
  const token = "";
  if (token) return false;
  
  // Don't show on admin pages
  if (window.location.pathname.startsWith('/admin')) return false;
  
  if (hasEmailCaptured()) return false;
  
  const lastShown = localStorage.getItem('oc_popup_last_shown');
  if (lastShown) {
    const hoursSinceShown = (Date.now() - parseInt(lastShown)) / (1000 * 60 * 60);
    // Don't show again for 24 hours
    if (hoursSinceShown < 24) return false;
  }
  
  return true;
};

// Mark popup as shown
export const markPopupShown = () => {
  localStorage.setItem('oc_popup_last_shown', Date.now().toString());
};

export default {
  initFacebookPixel,
  initGoogleAnalytics,
  initGoogleAds,
  trackGoogleAdsConversion,
  trackClickToCall,
  trackEstimateConversion,
  initAllTracking,
  trackPageView,
  trackViewContent,
  trackInitiateCheckout,
  trackLead,
  trackSchedule,
  trackPurchase,
  trackCompleteRegistration,
  trackEmailCapture,
  trackCustomEvent,
  getVisitorId,
  trackVisitorBehavior,
  getVisitorHistory,
  hasEmailCaptured,
  setEmailCaptured,
  shouldShowEmailPopup,
  markPopupShown
};
